import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Truck, Upload, CheckCircle, Clock, FileText, Container as Trailer, Shield, ClipboardList, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import TermsConsentCheckbox from '../components/TermsConsentCheckbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const Field = ({ label, testid, children, className = '' }) => (
  <div className={className}>
    <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</label>
    {React.cloneElement(children, { 'data-testid': testid })}
  </div>
);

const inputClass =
  'w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none';

const SectionHeader = ({ icon: Icon, title, hint }) => (
  <div className="pt-4 border-t border-slate-200 mb-4">
    <div className="flex items-center gap-2">
      <Icon className="w-5 h-5 text-[#002FA7]" strokeWidth={2} />
      <h3 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{title}</h3>
    </div>
    {hint && <p className="text-xs text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{hint}</p>}
  </div>
);

const TruckFields = ({ truck, onChange }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
    <Field label="Year" testid="truck-year">
      <input type="number" min="1980" max={new Date().getFullYear() + 1} value={truck.year}
        onChange={(e) => onChange({ ...truck, year: e.target.value })} className={inputClass} />
    </Field>
    <Field label="Make" testid="truck-make">
      <input type="text" value={truck.make}
        onChange={(e) => onChange({ ...truck, make: e.target.value })} className={inputClass} />
    </Field>
    <Field label="Model" testid="truck-model">
      <input type="text" value={truck.model}
        onChange={(e) => onChange({ ...truck, model: e.target.value })} className={inputClass} />
    </Field>
    <Field label="VIN" testid="truck-vin" className="md:col-span-2">
      <input type="text" maxLength={17} value={truck.vin}
        onChange={(e) => onChange({ ...truck, vin: e.target.value.toUpperCase() })} className={inputClass} />
    </Field>
    <Field label="Fuel Type" testid="truck-fuel">
      <select value={truck.fuel_type} onChange={(e) => onChange({ ...truck, fuel_type: e.target.value })} className={inputClass}>
        <option value="diesel">Diesel</option>
        <option value="gas">Gas</option>
        <option value="electric">Electric</option>
        <option value="other">Other</option>
      </select>
    </Field>
    <Field label="License Plate #" testid="truck-plate">
      <input type="text" value={truck.license_plate}
        onChange={(e) => onChange({ ...truck, license_plate: e.target.value.toUpperCase() })} className={inputClass} />
    </Field>
    <Field label="Plate State" testid="truck-plate-state">
      <input type="text" maxLength={2} value={truck.license_plate_state}
        onChange={(e) => onChange({ ...truck, license_plate_state: e.target.value.toUpperCase() })} className={inputClass} placeholder="e.g. GA" />
    </Field>
    <Field label="GVWR (lbs)" testid="truck-gvwr">
      <input type="number" value={truck.gvwr_lbs}
        onChange={(e) => onChange({ ...truck, gvwr_lbs: e.target.value })} className={inputClass} />
    </Field>
    <Field label="IRP Account #" testid="truck-irp">
      <input type="text" value={truck.irp_account}
        onChange={(e) => onChange({ ...truck, irp_account: e.target.value })} className={inputClass} placeholder="Optional" />
    </Field>
    <Field label="IFTA Account #" testid="truck-ifta">
      <input type="text" value={truck.ifta_account}
        onChange={(e) => onChange({ ...truck, ifta_account: e.target.value })} className={inputClass} placeholder="Optional" />
    </Field>
  </div>
);

const TrailerFields = ({ trailer, onChange }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
    <Field label="Year" testid="trailer-year">
      <input type="number" min="1980" max={new Date().getFullYear() + 1} value={trailer.year}
        onChange={(e) => onChange({ ...trailer, year: e.target.value })} className={inputClass} />
    </Field>
    <Field label="Make" testid="trailer-make">
      <input type="text" value={trailer.make}
        onChange={(e) => onChange({ ...trailer, make: e.target.value })} className={inputClass} />
    </Field>
    <Field label="Trailer Type" testid="trailer-type">
      <select value={trailer.trailer_type} onChange={(e) => onChange({ ...trailer, trailer_type: e.target.value })} className={inputClass}>
        <option value="dry_van">Dry Van</option>
        <option value="reefer">Reefer</option>
        <option value="flatbed">Flatbed</option>
        <option value="tanker">Tanker</option>
        <option value="other">Other</option>
      </select>
    </Field>
    <Field label="VIN" testid="trailer-vin" className="md:col-span-2">
      <input type="text" maxLength={17} value={trailer.vin}
        onChange={(e) => onChange({ ...trailer, vin: e.target.value.toUpperCase() })} className={inputClass} />
    </Field>
    <Field label="License Plate #" testid="trailer-plate">
      <input type="text" value={trailer.license_plate}
        onChange={(e) => onChange({ ...trailer, license_plate: e.target.value.toUpperCase() })} className={inputClass} />
    </Field>
    <Field label="Plate State" testid="trailer-plate-state">
      <input type="text" maxLength={2} value={trailer.license_plate_state}
        onChange={(e) => onChange({ ...trailer, license_plate_state: e.target.value.toUpperCase() })} className={inputClass} placeholder="e.g. GA" />
    </Field>
  </div>
);

const PolicyFields = ({ policy, onChange }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
    <Field label="Insurance Carrier" testid="policy-carrier">
      <input type="text" value={policy.carrier_name}
        onChange={(e) => onChange({ ...policy, carrier_name: e.target.value })} className={inputClass} placeholder="e.g. Progressive Commercial" />
    </Field>
    <Field label="Policy Number" testid="policy-number">
      <input type="text" value={policy.policy_number}
        onChange={(e) => onChange({ ...policy, policy_number: e.target.value })} className={inputClass} />
    </Field>
    <Field label="Expiry Date" testid="policy-expiry">
      <input type="date" value={policy.expiry_date}
        onChange={(e) => onChange({ ...policy, expiry_date: e.target.value })} className={inputClass} />
    </Field>
    <Field label="Coverage Amount (USD)" testid="policy-amount">
      <input type="number" min="0" step="1000" value={policy.coverage_amount_usd}
        onChange={(e) => onChange({ ...policy, coverage_amount_usd: e.target.value })} className={inputClass} placeholder="e.g. 100000" />
    </Field>
  </div>
);

const OwnerOperatorSection = ({ form, setForm }) => (
  <>
    <SectionHeader icon={Truck} title="Truck Information" hint="Details of the power unit you'll be running." />
    <TruckFields truck={form.truck} onChange={(next) => setForm({ ...form, truck: next })} />

    <div className="flex items-center gap-3 mb-4">
      <input
        id="has_trailer"
        type="checkbox"
        data-testid="has-trailer-toggle"
        checked={form.has_trailer}
        onChange={(e) => setForm({ ...form, has_trailer: e.target.checked })}
        className="w-4 h-4 accent-[#002FA7]"
      />
      <label htmlFor="has_trailer" className="text-sm font-semibold text-slate-900 cursor-pointer" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        I own a trailer
      </label>
    </div>
    {form.has_trailer && (
      <>
        <SectionHeader icon={Trailer} title="Trailer Information" />
        <TrailerFields trailer={form.trailer} onChange={(next) => setForm({ ...form, trailer: next })} />
      </>
    )}

    <SectionHeader icon={Shield} title="Physical Damage Insurance"
      hint="Coverage protecting your equipment. You can leave this blank now and add it later." />
    <PolicyFields policy={form.physical_damage_policy}
      onChange={(next) => setForm({ ...form, physical_damage_policy: next })} />
  </>
);

const CompanyDriverSection = ({ form, setForm, emptyEmployment }) => {
  const employment = form.past_employment || [];
  const addEntry = () => setForm({ ...form, past_employment: [...employment, { ...emptyEmployment }] });
  const removeEntry = (idx) => setForm({ ...form, past_employment: employment.filter((_, i) => i !== idx) });
  const updateEntry = (idx, next) => setForm({
    ...form,
    past_employment: employment.map((e, i) => i === idx ? next : e),
  });
  return (
    <>
      <SectionHeader icon={ClipboardList} title="Employment History (Past 3 Years)"
        hint="Required by FMCSA Part 391 — list every employer, gap-free. Add all positions held in the last 3 years." />
      {employment.length === 0 && (
        <p className="text-sm text-slate-500 italic mb-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          No employment entries yet. Add your most recent position first.
        </p>
      )}
      <div className="space-y-4 mb-4">
        {employment.map((entry, idx) => (
          <div key={idx} data-testid={`employment-entry-${idx}`} className="border border-slate-200 rounded-md p-4 bg-slate-50">
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Employer #{idx + 1}</p>
              <button type="button" onClick={() => removeEntry(idx)}
                data-testid={`employment-remove-${idx}`}
                className="text-red-600 hover:text-red-800">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Company Name *" testid={`emp-company-${idx}`}>
                <input type="text" required value={entry.company_name}
                  onChange={(e) => updateEntry(idx, { ...entry, company_name: e.target.value })}
                  className={inputClass} />
              </Field>
              <Field label="Position" testid={`emp-position-${idx}`}>
                <input type="text" value={entry.position || ''}
                  onChange={(e) => updateEntry(idx, { ...entry, position: e.target.value })}
                  className={inputClass} placeholder="e.g. OTR Driver" />
              </Field>
              <Field label="Start (YYYY-MM) *" testid={`emp-start-${idx}`}>
                <input type="month" required value={entry.start_date || ''}
                  onChange={(e) => updateEntry(idx, { ...entry, start_date: e.target.value })}
                  className={inputClass} />
              </Field>
              <Field label="End (leave blank if current)" testid={`emp-end-${idx}`}>
                <input type="month" value={entry.end_date || ''}
                  onChange={(e) => updateEntry(idx, { ...entry, end_date: e.target.value })}
                  className={inputClass} />
              </Field>
              <Field label="Reason for Leaving" testid={`emp-reason-${idx}`} className="md:col-span-2">
                <input type="text" value={entry.reason_for_leaving || ''}
                  onChange={(e) => updateEntry(idx, { ...entry, reason_for_leaving: e.target.value })}
                  className={inputClass} placeholder="e.g. Better pay, seeking OTR routes" />
              </Field>
              <Field label="Supervisor Name" testid={`emp-supervisor-${idx}`}>
                <input type="text" value={entry.supervisor_name || ''}
                  onChange={(e) => updateEntry(idx, { ...entry, supervisor_name: e.target.value })}
                  className={inputClass} />
              </Field>
              <Field label="Supervisor Phone" testid={`emp-supervisor-phone-${idx}`}>
                <input type="tel" value={entry.supervisor_phone || ''}
                  onChange={(e) => updateEntry(idx, { ...entry, supervisor_phone: e.target.value })}
                  className={inputClass} />
              </Field>
              <Field label="Company Address" testid={`emp-address-${idx}`} className="md:col-span-2">
                <input type="text" value={entry.company_address || ''}
                  onChange={(e) => updateEntry(idx, { ...entry, company_address: e.target.value })}
                  className={inputClass} placeholder="Street, City, State, ZIP" />
              </Field>
              <label className="md:col-span-2 flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={!!entry.dot_regulated}
                  onChange={(e) => updateEntry(idx, { ...entry, dot_regulated: e.target.checked })}
                  data-testid={`emp-dot-${idx}`}
                  className="w-4 h-4 accent-[#002FA7]" />
                DOT-regulated safety-sensitive position (required disclosure per 49 CFR §391.23).
              </label>
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={addEntry}
        data-testid="employment-add"
        className="text-sm text-[#002FA7] hover:text-[#00247D] font-semibold flex items-center gap-1">
        <Plus className="w-4 h-4" /> Add employer
      </button>
    </>
  );
};

const DocumentUploadCard = ({ testid, inputTestid, docType, label, hasFile, fileName, uploading, onUpload, helpText }) => (
  <div data-testid={testid} className="border border-slate-200 rounded-md p-5">
    <div className="flex items-center gap-3 mb-3">
      <FileText className="w-6 h-6 text-[#002FA7]" strokeWidth={2} />
      <div className="flex-1">
        <p className="font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</p>
        {hasFile ? (
          <p className="text-xs text-green-600 mt-1 flex items-center gap-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <CheckCircle className="w-3 h-3" /> {fileName}
          </p>
        ) : helpText ? (
          <p className="text-xs text-slate-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{helpText}</p>
        ) : null}
      </div>
    </div>
    <label className="block">
      <span className="sr-only">Choose {label} file</span>
      <input
        type="file"
        data-testid={inputTestid}
        accept="image/*,.pdf"
        onChange={(e) => onUpload(docType, e.target.files[0])}
        disabled={uploading}
        className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#002FA7] file:text-white hover:file:bg-[#00247D] disabled:opacity-50"
      />
    </label>
    {uploading && <p className="text-xs text-slate-500 mt-2">Uploading...</p>}
  </div>
);

const DriverApplication = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [driver, setDriver] = useState(null);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [needsTerms, setNeedsTerms] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  useEffect(() => {
    axios.get(`${API}/legal/acceptance-status`, { withCredentials: true })
      .then((r) => setNeedsTerms((r.data.required || []).length > 0)).catch(() => {});
  }, []);
  const [showSetPw, setShowSetPw] = useState(false);
  const [pw, setPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwDone, setPwDone] = useState(false);
  const [pwError, setPwError] = useState('');
  const [draftRestored, setDraftRestored] = useState(false);
  const draftReady = useRef(false);
  const lastStep = useRef('personal');

  const handleSetPassword = async () => {
    setPwError('');
    if ((pw || '').length < 8) { setPwError('Password must be at least 8 characters.'); return; }
    setPwSaving(true);
    try {
      await axios.post(`${API}/driver/set-password`, { password: pw }, { withCredentials: true });
      setPwDone(true);
      setTimeout(() => setShowSetPw(false), 1500);
    } catch (err) {
      const d = err.response?.data?.detail;
      setPwError(typeof d === 'string' ? d : 'Could not set password.');
    } finally {
      setPwSaving(false);
    }
  };
  const [uploading, setUploading] = useState({
    cdl: false, medical: false,
    truck_registration: false, physical_damage_insurance: false, w9_form: false,
  });

  const emptyTruck = {
    year: '', make: '', model: '', vin: '',
    license_plate: '', license_plate_state: '',
    fuel_type: 'diesel', gvwr_lbs: '',
    irp_account: '', ifta_account: '',
  };
  const emptyTrailer = {
    year: '', make: '', trailer_type: 'dry_van', vin: '',
    license_plate: '', license_plate_state: '',
  };
  const emptyPolicy = {
    carrier_name: '', policy_number: '',
    expiry_date: '', coverage_amount_usd: '',
  };
  const emptyEmployment = {
    company_name: '', position: '',
    start_date: '', end_date: '',
    reason_for_leaving: '',
    supervisor_name: '', supervisor_phone: '',
    company_address: '', dot_regulated: false,
  };

  const [form, setForm] = useState({
    full_name: '',
    date_of_birth: '',
    phone: '',
    ssn_last_4: '',
    cdl_number: '',
    cdl_class: 'A',
    cdl_expiration: '',
    us_work_authorization: true,
    driver_type: 'company_driver',
    // Owner-operator specific
    truck: { ...emptyTruck },
    has_trailer: false,
    trailer: { ...emptyTrailer },
    physical_damage_policy: { ...emptyPolicy },
    // Company-driver specific
    past_employment: [],
  });

  useEffect(() => {
    if (user && user.role !== 'driver') {
      navigate('/dashboard');
      return;
    }
    fetchProfile();
  }, [user]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/drivers/me`, { withCredentials: true });
      setDriver(res.data.driver);
      setProfile(res.data.profile);
      if (res.data.profile) {
        const p = res.data.profile;
        setForm({
          full_name: p.full_name || user?.name || '',
          date_of_birth: p.date_of_birth || '',
          phone: p.phone || '',
          ssn_last_4: p.ssn_last_4 || '',
          cdl_number: p.cdl_number || '',
          cdl_class: p.cdl_class || 'A',
          cdl_expiration: p.cdl_expiration || '',
          us_work_authorization: p.us_work_authorization ?? true,
          driver_type: p.driver_type || 'company_driver',
          truck: { ...emptyTruck, ...(p.truck || {}) },
          has_trailer: !!p.has_trailer,
          trailer: { ...emptyTrailer, ...(p.trailer || {}) },
          physical_damage_policy: { ...emptyPolicy, ...(p.physical_damage_policy || {}) },
          past_employment: Array.isArray(p.past_employment) ? p.past_employment : [],
        });
      } else if (res.data.application_draft?.data && Object.keys(res.data.application_draft.data).length) {
        const d = res.data.application_draft.data;
        setForm(f => ({ ...f, ...d, full_name: d.full_name || user?.name || '' }));
        setDraftRestored(true);
      } else {
        setForm(f => ({ ...f, full_name: user?.name || '' }));
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
      draftReady.current = true;
      setTimeout(() => {
        const id = (window.location.hash || '').replace('#', '');
        if (id.startsWith('step-')) document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 250);
    }
  };

  // Autosave the in-progress application (debounced) + remember the last section touched,
  // so a stalled driver can be nudged straight back to where they left off.
  const stepFor = (el) => {
    const heads = Array.from(document.querySelectorAll('[id^="step-"]'));
    const y = el?.getBoundingClientRect?.().top ?? 0;
    let current = 'personal';
    heads.forEach((h) => { if (h.getBoundingClientRect().top <= y) current = h.id.replace('step-', ''); });
    return current;
  };
  const noteStep = (e) => { lastStep.current = stepFor(e.target); };
  useEffect(() => {
    if (!draftReady.current || profile || driver?.status !== 'registered') return undefined;
    const t = setTimeout(() => {
      axios.put(`${API}/drivers/me/application-draft`, { step: lastStep.current, data: form }, { withCredentials: true }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (form.ssn_last_4.length !== 4 || !/^\d{4}$/.test(form.ssn_last_4)) {
      setError('SSN last 4 must be 4 digits');
      return;
    }

    setSubmitting(true);
    try {
      // Coerce string numbers → numbers where present; strip empty strings so
      // Pydantic sees None (fields are optional).
      const clean = (obj, numFields = []) => {
        const out = {};
        for (const [k, v] of Object.entries(obj || {})) {
          if (v === '' || v === null || v === undefined) continue;
          out[k] = numFields.includes(k) ? Number(v) : v;
        }
        return out;
      };
      const payload = {
        full_name: form.full_name,
        date_of_birth: form.date_of_birth,
        phone: form.phone,
        ssn_last_4: form.ssn_last_4,
        cdl_number: form.cdl_number,
        cdl_class: form.cdl_class,
        cdl_expiration: form.cdl_expiration,
        us_work_authorization: form.us_work_authorization,
        driver_type: form.driver_type,
      };
      if (form.driver_type === 'owner_operator') {
        const truck = clean(form.truck, ['year', 'gvwr_lbs']);
        if (Object.keys(truck).length > 0) payload.truck = truck;
        payload.has_trailer = !!form.has_trailer;
        if (form.has_trailer) {
          const trailer = clean(form.trailer, ['year']);
          if (Object.keys(trailer).length > 0) payload.trailer = trailer;
        }
        const policy = clean(form.physical_damage_policy, ['coverage_amount_usd']);
        if (Object.keys(policy).length > 0) payload.physical_damage_policy = policy;
      } else {
        payload.past_employment = (form.past_employment || [])
          .filter((e) => e.company_name && e.start_date)
          .map((e) => ({ ...e, dot_regulated: !!e.dot_regulated }));
      }

      if (needsTerms) payload.accept_terms = acceptTerms;
      const res = await axios.post(`${API}/drivers/me/profile`, payload, { withCredentials: true });
      setNeedsTerms(false);
      setSuccess('Application submitted. Status: ' + res.data.status);
      setShowSetPw(true);
      const meRes = await axios.get(`${API}/drivers/me`, { withCredentials: true });
      setDriver(meRes.data.driver);
      setProfile(meRes.data.profile);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDocumentUpload = async (docType, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }
    const uploadKey =
      docType === 'cdl_license' ? 'cdl' :
      docType === 'medical_card' ? 'medical' :
      docType;
    setUploading(u => ({ ...u, [uploadKey]: true }));
    setError('');
    try {
      const base64 = await fileToBase64(file);
      lastStep.current = 'documents';
      if (!profile && driver?.status === 'registered') {
        axios.put(`${API}/drivers/me/application-draft`, { step: 'documents', data: form }, { withCredentials: true }).catch(() => {});
      }
      await axios.post(
        `${API}/drivers/me/documents`,
        {
          document_type: docType,
          file_name: file.name,
          file_data: base64,
          content_type: file.type,
        },
        { withCredentials: true }
      );
      const label =
        docType === 'cdl_license' ? 'CDL License' :
        docType === 'medical_card' ? 'Medical Card' :
        docType === 'truck_registration' ? 'Truck Registration' :
        docType === 'physical_damage_insurance' ? 'Physical Damage Insurance' :
        docType === 'w9_form' ? 'W-9 Form' : docType;
      setSuccess(`${label} uploaded successfully`);
      const res = await axios.get(`${API}/drivers/me`, { withCredentials: true });
      setDriver(res.data.driver);
      setProfile(res.data.profile);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Upload failed');
    } finally {
      setUploading(u => ({ ...u, [uploadKey]: false }));
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F2F4F7]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading...</div>;
  }

  if (driver?.status === 'active' && profile?.correction_request?.status !== 'open') {
    return (
      <div className="min-h-screen bg-[#F2F4F7] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" strokeWidth={2} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>You're Active!</h1>
          <p className="text-sm text-slate-600 mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Your driver application has been approved.</p>
          <button
            data-testid="go-to-dashboard-button"
            onClick={() => navigate('/dashboard')}
            className="w-full bg-[#002FA7] text-white py-3 px-4 rounded-md font-semibold hover:bg-[#00247D] transition-all"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const hasCdl = profile?.documents?.cdl_license?.has_file;
  const hasMedical = profile?.documents?.medical_card?.has_file;

  return (
    <div className="min-h-screen bg-[#F2F4F7]">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="px-6 py-4 flex items-center justify-between max-w-5xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#002FA7] rounded-md flex items-center justify-center">
              <Truck className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>CivilX TMS</h1>
              <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Driver Application</p>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate('/login'); }}
            className="text-sm text-slate-600 hover:text-slate-900"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            Logout
          </button>
        </div>
      </header>
      {profile?.correction_request?.status === 'open' && (
        <div className="max-w-5xl mx-auto px-6 pt-4">
          <div className="rounded-md bg-amber-50 border border-amber-300 px-4 py-3 text-sm text-amber-900" data-testid="driver-correction-banner" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <strong>Your company asked you to correct your application:</strong> “{profile.correction_request.note}”. Update the information below and resubmit — your account and status are preserved.
          </div>
        </div>
      )}


      <main className="max-w-5xl mx-auto p-6 md:p-8">
        <div className="mb-6">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Employment Application</h2>
          <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Complete your application and upload required documents.</p>
        </div>

        {driver?.status === 'pending_approval' && (
          <div data-testid="pending-approval-banner" className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-md flex items-center gap-3">
            <Clock className="w-5 h-5 text-orange-600" strokeWidth={2} />
            <div>
              <p className="text-sm font-semibold text-orange-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Application Submitted — Pending Approval</p>
              <p className="text-xs text-orange-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Your company admin will review and approve your application.</p>
            </div>
          </div>
        )}

        {error && (
          <div data-testid="application-error-message" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
            {error}
          </div>
        )}

        {success && (
          <div data-testid="application-success-message" className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
            {success}
          </div>
        )}

        {showSetPw && (
          <div data-testid="set-password-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Set a password (optional)
              </h3>
              <p className="text-sm text-slate-600 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Your application is in. Set a password so you can log back in anytime — or skip and keep using your invite link.
              </p>
              {pwDone ? (
                <p data-testid="set-password-done" className="text-sm font-semibold text-green-700">Password saved! You can now log in with your email.</p>
              ) : (
                <>
                  <input
                    type="password"
                    data-testid="set-password-input"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="New password (min 8 characters)"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none mb-2"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  />
                  {pwError && <p data-testid="set-password-error" className="text-xs text-red-600 mb-2">{pwError}</p>}
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <button
                      type="button"
                      data-testid="skip-set-password"
                      onClick={() => setShowSetPw(false)}
                      className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-md"
                    >Skip for now</button>
                    <button
                      type="button"
                      data-testid="save-set-password"
                      onClick={handleSetPassword}
                      disabled={pwSaving}
                      className="px-4 py-2 text-sm font-semibold bg-[#002FA7] text-white rounded-md hover:bg-[#00247D] disabled:opacity-50"
                    >{pwSaving ? 'Saving…' : 'Save password'}</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} onFocusCapture={noteStep} className="bg-white border border-slate-200 rounded-md p-6 md:p-8 mb-6">
          {draftRestored && (
            <div data-testid="draft-restored-banner" className="mb-5 p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Welcome back — we saved what you entered last time. Pick up where you left off and submit when you're done.
            </div>
          )}
          <h3 id="step-personal" className="text-xl font-semibold tracking-tight text-slate-900 mb-6 scroll-mt-24" style={{ fontFamily: 'Work Sans, sans-serif' }}>Personal Information</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Full Name</label>
              <input
                type="text"
                data-testid="app-full-name-input"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Date of Birth</label>
              <input
                type="date"
                data-testid="app-dob-input"
                value={form.date_of_birth}
                onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Email</label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full px-3 py-2 border border-slate-300 rounded-md bg-slate-50 text-slate-500"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Phone</label>
              <input
                type="tel"
                data-testid="app-phone-input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>SSN (Last 4 Digits)</label>
              <input
                type="text"
                data-testid="app-ssn-input"
                value={form.ssn_last_4}
                onChange={(e) => setForm({ ...form, ssn_last_4: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                maxLength={4}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                required
                placeholder="1234"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>US Work Authorization</label>
              <select
                data-testid="app-work-auth-select"
                value={form.us_work_authorization ? 'yes' : 'no'}
                onChange={(e) => setForm({ ...form, us_work_authorization: e.target.value === 'yes' })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>

          <h3 id="step-cdl" className="text-xl font-semibold tracking-tight text-slate-900 mb-6 pt-4 border-t border-slate-200 scroll-mt-24" style={{ fontFamily: 'Work Sans, sans-serif' }}>CDL Information</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>CDL Number</label>
              <input
                type="text"
                data-testid="app-cdl-number-input"
                value={form.cdl_number}
                onChange={(e) => setForm({ ...form, cdl_number: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>CDL Class</label>
              <select
                data-testid="app-cdl-class-select"
                value={form.cdl_class}
                onChange={(e) => setForm({ ...form, cdl_class: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                <option value="A">Class A</option>
                <option value="B">Class B</option>
                <option value="C">Class C</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>CDL Expiration</label>
              <input
                type="date"
                data-testid="app-cdl-expiration-input"
                value={form.cdl_expiration}
                onChange={(e) => setForm({ ...form, cdl_expiration: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                required
              />
            </div>
          </div>

          <h3 id="step-type" className="text-xl font-semibold tracking-tight text-slate-900 mb-6 pt-4 border-t border-slate-200 scroll-mt-24" style={{ fontFamily: 'Work Sans, sans-serif' }}>Driver Type</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <label
              data-testid="driver-type-company"
              className={`flex items-start gap-3 p-4 border rounded-md cursor-pointer transition-all ${
                form.driver_type === 'company_driver' ? 'border-[#002FA7] bg-blue-50' : 'border-slate-300 bg-white hover:bg-slate-50'
              }`}
            >
              <input
                type="radio"
                name="driver_type"
                value="company_driver"
                checked={form.driver_type === 'company_driver'}
                onChange={(e) => setForm({ ...form, driver_type: e.target.value })}
                className="mt-1"
              />
              <div>
                <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Company Driver</p>
                <p className="text-xs text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Drive a company-owned vehicle. Receive standard employment benefits.</p>
              </div>
            </label>

            <label
              data-testid="driver-type-owner"
              className={`flex items-start gap-3 p-4 border rounded-md cursor-pointer transition-all ${
                form.driver_type === 'owner_operator' ? 'border-[#002FA7] bg-blue-50' : 'border-slate-300 bg-white hover:bg-slate-50'
              }`}
            >
              <input
                type="radio"
                name="driver_type"
                value="owner_operator"
                checked={form.driver_type === 'owner_operator'}
                onChange={(e) => setForm({ ...form, driver_type: e.target.value })}
                className="mt-1"
              />
              <div>
                <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Owner Operator</p>
                <p className="text-xs text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Operate your own vehicle as an independent contractor.</p>
              </div>
            </label>
          </div>

          {form.driver_type === 'owner_operator' && (
            <OwnerOperatorSection form={form} setForm={setForm} />
          )}

          {form.driver_type === 'company_driver' && (
            <CompanyDriverSection form={form} setForm={setForm} emptyEmployment={emptyEmployment} />
          )}

          {needsTerms && <TermsConsentCheckbox checked={acceptTerms} onChange={setAcceptTerms} testid="application-terms-checkbox" />}
          <button
            type="submit"
            data-testid="submit-application-button"
            disabled={submitting || (needsTerms && !acceptTerms)}
            className="bg-[#002FA7] text-white px-6 py-3 rounded-md font-semibold hover:bg-[#00247D] transition-all duration-200 disabled:opacity-50"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {submitting ? 'Saving...' : 'Save Application'}
          </button>
        </form>

        <div className="bg-white border border-slate-200 rounded-md p-6 md:p-8">
          <h3 id="step-documents" className="text-xl font-semibold tracking-tight text-slate-900 mb-2 scroll-mt-24" style={{ fontFamily: 'Work Sans, sans-serif' }}>Required Documents</h3>
          {form.driver_type === 'owner_operator' && (
            <p className="text-xs text-slate-600 mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Truck registration, physical damage insurance, and W-9 are strongly recommended before you start dispatching but you can <strong>submit the application now and upload them later</strong> from your dashboard.
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DocumentUploadCard
              testid="cdl-upload-card"
              inputTestid="cdl-file-input"
              docType="cdl_license"
              label="CDL License"
              hasFile={hasCdl}
              fileName={profile?.documents?.cdl_license?.file_name}
              uploading={uploading.cdl}
              onUpload={handleDocumentUpload}
            />
            <DocumentUploadCard
              testid="medical-upload-card"
              inputTestid="medical-file-input"
              docType="medical_card"
              label="Medical Card"
              hasFile={hasMedical}
              fileName={profile?.documents?.medical_card?.file_name}
              uploading={uploading.medical}
              onUpload={handleDocumentUpload}
            />
            {form.driver_type === 'owner_operator' && (
              <>
                <DocumentUploadCard
                  testid="truck-reg-upload-card"
                  inputTestid="truck-reg-file-input"
                  docType="truck_registration"
                  label="Truck Registration"
                  helpText="Optional — can be submitted later"
                  hasFile={!!profile?.documents?.truck_registration?.file_name}
                  fileName={profile?.documents?.truck_registration?.file_name}
                  uploading={uploading.truck_registration}
                  onUpload={handleDocumentUpload}
                />
                <DocumentUploadCard
                  testid="physical-damage-upload-card"
                  inputTestid="physical-damage-file-input"
                  docType="physical_damage_insurance"
                  label="Physical Damage Insurance"
                  helpText="Certificate for your truck/trailer. Can be submitted later."
                  hasFile={!!profile?.documents?.physical_damage_insurance?.file_name}
                  fileName={profile?.documents?.physical_damage_insurance?.file_name}
                  uploading={uploading.physical_damage_insurance}
                  onUpload={handleDocumentUpload}
                />
                <DocumentUploadCard
                  testid="w9-upload-card"
                  inputTestid="w9-file-input"
                  docType="w9_form"
                  label="IRS Form W-9"
                  helpText="Required for 1099 tax reporting."
                  hasFile={!!profile?.documents?.w9_form?.file_name}
                  fileName={profile?.documents?.w9_form?.file_name}
                  uploading={uploading.w9_form}
                  onUpload={handleDocumentUpload}
                />
              </>
            )}
          </div>

          <p className="text-xs text-slate-500 mt-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Accepted formats: JPG, PNG, PDF. Max 5MB each.
          </p>
        </div>
      </main>
    </div>
  );
};

export default DriverApplication;
